<template>
	<ul class="products-list">
		<li
			v-for="product in products"
			class="products-list-item"
			:class="{
				'in-cart': getProductsCountInCart(product.id),
				'has-no-image': !getProductImage(product)
			}"
			:key="product.id"
			@click="showProductDetails(product.id)"
		>
			<div
				class="products-list-holder"
				:class="{
					'products-list-holder--content-center': !product.description
				}"
			>
				<div>
					<span class="products-list-title">
						<span class="products-list-title-holder" v-html="product.name" />
					</span>

					<div v-if="product.description" class="products-list-description">
						<v-clamp autoresize :max-lines="2">
							{{ product.description }}
						</v-clamp>
					</div>
				</div>

				<div>
					<div class="products-list-price-holder">
						<div>
							<template
								v-if="
									product.promotedPrice !== null &&
										product.promotedPrice !== undefined
								"
							>
								<span class="products-list-price">
									<formatted-price :price="product.promotedPrice" />
								</span>
								<span
									v-if="
										isPromotedPriceLess(product.promotedPrice, product.price)
									"
									class="products-list-alt-price alt-price"
								>
									<formatted-price :price="product.price" decimal-height="70" />
								</span>
							</template>
							<span v-else class="products-list-price">
								<formatted-price :price="product.price" decimal-height="70" />
							</span>
						</div>
						<div class="ml-10">
							<span
								v-if="checkIsSoldOut(product)"
								class="product-badge sold-badge"
							>
								Sold out
							</span>
						</div>
					</div>
					<div class="product-badge-holder">
						<span
							v-if="
								product.trending &&
									product.trendingCategoryRating &&
									switcher === 'trending'
							"
							class="products-priority"
						>
							{{ product.trendingCategoryRating }}
						</span>
						<span v-if="product.new" class="product-badge">New</span>
						<span v-if="product.favorite" class="product-badge">
							Staff Favorites
						</span>
						<span v-if="checkIsAlmostGone(product)" class="product-badge">
							Almost Gone
						</span>
						<span v-if="checkIsLimitedQuantity(product)" class="product-badge">
							Limited Qty
						</span>
					</div>
				</div>
			</div>

			<ProductImage
				:image-url="getProductImage(product)"
				:placeholder-url="getProductPreview(product)"
				:isSold="checkIsSoldOut(product)"
			/>
			<ProductCart
				class="badge"
				:cart-items-count="getProductsCountInCart(product.remoteId)"
			/>
		</li>
	</ul>
</template>

<script>
import { mapState } from 'vuex'
import currency from 'currency.js'
import VClamp from 'vue-clamp'

import ProductImage from './ProductImage'
import ProductCart from './ProductCart'

import { REMAINING_STATUS } from '@/constants'
import FormattedPrice from '@/components/FormattedPrice.vue'

export default {
	name: 'ProductsList',

	components: {
		FormattedPrice,
		ProductImage,
		ProductCart,
		VClamp
	},

	props: {
		products: Array,
		switcher: {
			type: String,
			default: 'regular'
		}
	},

	computed: {
		...mapState('cart', ['cartItems']),
		...mapState('venue', ['venueInfo']),
		...mapState('ui', {
			isOneColumn: 'isMobileScreen'
		}),

		isEnableRemainingProduct() {
			return this.venueInfo.settings.remainingProductSettings.enableLabel
		},

		remainingStatus() {
			return REMAINING_STATUS
		}
	},

	methods: {
		getFormattedPrice(price) {
			return currency(price)
		},

		isPromotedPriceLess(promotedPrice, price) {
			return Number(promotedPrice) < Number(price)
		},

		getProductsCountInCart(remoteId) {
			return this.cartItems.reduce((acc, item) => {
				if (item.remoteProductId === remoteId) {
					return acc + item.quantity
				}
				return acc
			}, 0)
		},

		getProductImage(product) {
			const productGallery = product.gallery[0]

			return productGallery && productGallery.full ? productGallery.full : ''
		},

		getProductPreview(product) {
			const productGallery = product.gallery[0]

			return productGallery && productGallery.preview
				? productGallery.preview
				: ''
		},

		checkIsAlmostGone(product) {
			return product.remainingProductStatus === this.remainingStatus.ALMOST_GONE
		},

		checkIsLimitedQuantity(product) {
			return (
				product.remainingProductStatus === this.remainingStatus.LIMITED_QUANTITY
			)
		},

		checkIsSoldOut(product) {
			const { remainingProductStatus } = product
			const { EIGHTY_SIX, LIMIT_ALMOST_REACHED } = this.remainingStatus

			return [EIGHTY_SIX, LIMIT_ALMOST_REACHED].includes(remainingProductStatus)
		},

		showProductDetails(productId) {
			this.$router.push({
				...this.$route,
				query: { ...this.$route.query, productId }
			})
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.products-list {
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	margin: 0;
	padding: 0 0 17px;
	font-size: 11px;
	color: $white;
	list-style: none;

	@media (min-width: 768px) {
		padding: 0 0 17px;
	}

	@media (max-width: 1142px) {
		padding: 0 0 1px;
	}

	.products-list-price-holder {
		display: flex;
		align-items: center;

		.sold-badge {
			margin-top: 0;
		}
	}
}

.products-list-item {
	display: flex;
	justify-content: space-between;
	position: relative;
	width: 100%;
	min-height: 150px;
	cursor: pointer;
	border-bottom: 1px solid $border-color;
	overflow: hidden;
	transition: background-color 0.5s, box-shadow 0.5s;

	@media (min-width: 768px) {
		background-color: $deep-violet;
		width: calc(50% - 8px);
		min-height: 156px;
		border-radius: 12px;
		margin: 0 0 15px;
		border: 1px solid $border-color;
		box-shadow: 0px 2px 8px 0px $black;
		padding-left: 15px;
	}

	@media (min-width: 1024px) {
		&:hover {
			background-color: rgba($light-blue, 0.25);
			border-color: rgba($light-blue, 0.25);
			box-shadow: 0 2px 7px rgba(144, 125, 213, 0.25);

			.product-image {
				transform: scale(1.3);
			}
		}
	}

	&.has-no-image {
		.products-list-holder {
			width: 100%;
			padding-right: 15px;
		}

		.product-image-holder {
			display: none;
		}

		.cart {
			right: 16px;
			top: 16px;
		}
	}

	.product-image-holder {
		width: 124px;
		height: 124px;
		border-radius: 6px;
		margin: 15px 0 15px 15px;
		align-self: center;

		@media (min-width: 768px) {
			width: 156px;
			height: 156px;
			border-radius: 0;
			margin: 0;
		}
	}

	.product-badge {
		margin-top: 5px;
	}

	&.in-cart .product-image-holder:after {
		border-color: #4ac692;
	}

	.product-image-holder.has-no-image {
		margin: 0;
		height: auto;
		background: none;

		&:after {
			border: 0;
		}

		.product-image-badge-holder {
			position: static;
			text-align: right;
		}

		.badge:last-child {
			margin: 0;
		}
	}

	.product-image-badge-holder {
		left: auto;
		right: 8px;
		bottom: auto;
		top: 8px;
		transform: translateX(0);
	}

	.cart {
		position: absolute;
		right: 10px;
		top: 20px;

		@media (min-width: 768px) {
			top: 10px;
			right: 10px;
		}
	}

	.products-priority {
		margin: 5px 8px 0 0;
	}
}

.products-list-holder {
	width: calc(100% - 140px);
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 7px 0;

	@media (min-width: 640px) {
		width: calc(100% - 165px);
	}

	@media (min-width: 1360px) {
		padding: 10px 0 13px;
	}

	&--content-center {
		justify-content: center;
	}
}

.products-list-title {
	display: block;
	padding: 0 0 8px;
	width: 100%;
	font-size: 16px;
	font-weight: 500;

	@media (min-width: 640px) {
		font-size: 20px;
	}

	.highlighted-product-name {
		background-color: #816cc2;
	}
}

.product-badge-holder {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
}

.products-list-description {
	margin: 0 0 7px;
	font-size: 12px;
	color: $text-color;

	@media (min-width: 640px) {
		font-size: 14px;
	}
}

.products-list-price {
	display: inline-block;
	vertical-align: top;
	padding: 0 5px 0 0;
	font-weight: 600;
	font-size: 14px;

	@media (min-width: 640px) {
		font-size: 18px;
	}
}

.products-list-alt-price {
	display: inline-block;
	vertical-align: top;
	margin: 0 10px 0 0;
	font-size: 12px;
	font-weight: 700;
	color: rgba($white, 0.7);

	@media (min-width: 640px) {
		font-size: 14px;
		margin: -1px 10px 0 0;
	}
}

.product-image {
	transition: transform 0.5s;
}

@media (max-width: 767px) {
	.products-list-description {
		margin: 0 0 8px;
	}
}
</style>
